import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "modal-body call-panel-body",
  style: {"flex":"1","overflow-y":"auto"}
}
const _hoisted_2 = {
  key: 0,
  class: "email-loading-state"
}
const _hoisted_3 = {
  key: 1,
  class: "email-disconnected-state"
}
const _hoisted_4 = { class: "email-disconnected-content" }
const _hoisted_5 = {
  key: 2,
  class: "email-layout-with-sidebar"
}
const _hoisted_6 = {
  key: 0,
  class: "email-sidebar-header"
}
const _hoisted_7 = {
  key: 3,
  class: "email-main-content"
}
const _hoisted_8 = {
  key: 2,
  class: "email-empty-state"
}

import { ref, computed, watch, nextTick, onUnmounted } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import ConversationList from "@/components/bucket/interactions/ConversationList.vue";
import EmailComposer from "@/components/socials/EmailComposer.vue";
import EmailViewer from "@/components/socials/EmailViewer.vue";
import OutreachCueCards from "@/components/bucket/ai/OutreachCueCards.vue";
import AgentReplyDraft from "@/components/agent/AgentReplyDraft.vue";
import DeleteConfirmationModal from "@/components/modal/DeleteConfirmationModal.vue";
import { Button } from "@/components/ui";
import Unipile from "@/services/unipile/unipile";
import Contact from "@/services/contact/contact";
import AIGenerationService from "@/services/ai/ai-generation";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";
import { normalizeEmailThread } from "@/utils/conversationNormalizer";
import { useToast } from "@/composables/ui/useToast";
import { useBillingStatus } from "@/composables/billing/useBillingStatus";

import {
  useEmailConversations,
  useSendEmail,
  useDeleteEmail,
  useUpdateEmailReadStatus,
  useSaveDraft,
  useScheduleEmail,
  useEmailWebhooks,
} from "@/composables/emails";

// ─── Props ───────────────────────────────────────────────────────────────────
interface Props {
  selectedBucket: any;
  emailChannel: any;
  icps: Array<{ id: string; name: string; isDefault?: boolean }>;
  isActive?: boolean;
  emailAccounts: any[];
  isChannelsLoading?: boolean;
  icpLoading?: boolean;
  pendingCueCardContent?: any;
  initialThreadId?: string | null;
}

interface Emits {
  (e: "refresh-conversations"): void;
  (e: "email-sent"): void;
  (e: "connect-account"): void;
  (e: "open-ai-personalization", payload: any): void;
  (e: "switch-channel", payload: any): void;
  (e: "content-applied"): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EmailInteractionPanel',
  props: {
    selectedBucket: {},
    emailChannel: { default: null },
    icps: {},
    isActive: { type: Boolean, default: true },
    emailAccounts: { default: () => [] },
    isChannelsLoading: { type: Boolean, default: false },
    icpLoading: { type: Boolean, default: false },
    pendingCueCardContent: { default: null },
    initialThreadId: { default: null }
  },
  emits: ["refresh-conversations", "email-sent", "connect-account", "open-ai-personalization", "switch-channel", "content-applied"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// ─── Emits ───────────────────────────────────────────────────────────────────
const emit = __emit;

// ─── Global utilities ────────────────────────────────────────────────────────
const $toast = useToast();
const resolvedAiPromptsQuery = useResolvedAiPrompts();
const queryClient = useQueryClient();
const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

// ─── Template refs ───────────────────────────────────────────────────────────
const emailViewerRef = ref<InstanceType<typeof EmailViewer> | null>(null);
const emailComposerRef = ref<InstanceType<typeof EmailComposer> | null>(null);

// ─── Local reactive state ────────────────────────────────────────────────────
const showEmailComposer = ref(false);
const replyingToEmail = ref<any>(null);
const isReplyAll = ref(false);
const forwardingEmail = ref<any>(null);
const selectedEmailThreadId = ref<string | null>(null);
const showEmailDetailView = ref(false);
const isLoadingMessages = ref(false);
const selectedEmailThread = ref<any>(null);
const isGeneratingAI = ref(false);
const generatingAction = ref<"reply" | "followup" | null>(null);
const aiGeneratedContent = ref("");
const aiGeneratedSubject = ref("");
const showDeleteConfirmation = ref(false);
const emailToDelete = ref<any>(null);

// ─── Computed: current account ───────────────────────────────────────────────
const currentEmailAccount = computed(() => {
  if (props.emailChannel?.accountId) {
    return (
      props.emailAccounts.find((acc: any) => acc.id === props.emailChannel.accountId) ||
      props.emailAccounts[0] ||
      null
    );
  }
  return props.emailAccounts[0] || null;
});

const isEmailAccountConnected = computed(() => {
  if (!currentEmailAccount.value) return false;
  if (!currentEmailAccount.value.status) return true;
  const status = currentEmailAccount.value.status.toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(status);
});

const isEmailChannelDisconnected = computed(() => {
  if (!props.emailChannel) return true;
  if (!props.emailChannel.isConnected) return true;
  const status = props.emailChannel.status?.toUpperCase();
  return ["DISCONNECTED", "ERROR"].includes(status || "");
});

// Every connected email account the user has — the list merges threads from all of
// them, since a contact's reply can land in any inbox (and the Buckets table's
// unread-email badge counts across all of them).
const connectedEmailAccountIds = computed<string[]>(() => {
  const ids = props.emailAccounts
    .filter((acc: any) => {
      if (!acc?.id) return false;
      if (!acc.status) return true;
      const status = acc.status.toUpperCase();
      return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(status);
    })
    .map((acc: any) => acc.id);
  if (ids.length === 0 && currentEmailAccount.value?.id) return [currentEmailAccount.value.id];
  return ids;
});

// The account that owns a given thread/email. List items are tagged with `accountId`
// by useEmailConversations; individual messages carry Unipile's own `account_id`.
function accountIdFor(email: any): string | undefined {
  return (
    email?.accountId ||
    email?.account_id ||
    selectedEmailThread.value?.accountId ||
    currentEmailAccount.value?.id
  );
}

// Replies/forwards must go out from the account that owns the parent email —
// EmailComposer drops reply_to if the From account doesn't match it.
const composerAccountId = computed(() => {
  const parent = replyingToEmail.value || forwardingEmail.value;
  return parent ? accountIdFor(parent) : currentEmailAccount.value?.id;
});

// ─── Computed query params ───────────────────────────────────────────────────
const conversationsParams = computed(() => {
  const contact = props.selectedBucket?.contact;
  const accountIds = connectedEmailAccountIds.value;
  if (accountIds.length === 0) return null;

  const allEmails = [
    ...new Set([contact?.email, ...(contact?.additionalEmails || [])].filter(Boolean) as string[]),
  ];
  if (allEmails.length === 0) return null;

  return { accountIds, contactEmails: allEmails, limit: 50 };
});

// ─── TanStack Queries ────────────────────────────────────────────────────────
const { data: conversationsData, isLoading: isLoadingConversations } =
  useEmailConversations(conversationsParams);

// ─── TanStack Mutations ─────────────────────────────────────────────────────
const { sendEmail } = useSendEmail();
const { deleteEmail } = useDeleteEmail();
const { updateReadStatus } = useUpdateEmailReadStatus();
const { saveDraft } = useSaveDraft();
const { scheduleEmail } = useScheduleEmail();

// ─── Derived state ──────────────────────────────────────────────────────────
const emailConversations = computed(() => conversationsData.value || []);

const computedSelectedEmailThread = computed(() => {
  if (selectedEmailThread.value) return selectedEmailThread.value;
  if (!selectedEmailThreadId.value) return emailConversations.value[0] || null;
  return (
    emailConversations.value.find((t: any) => t.id === selectedEmailThreadId.value) ||
    emailConversations.value[0] ||
    null
  );
});

// Every id the open thread's emails go by, to match the AI reply drafts answering them.
const selectedThreadEmailIds = computed<string[]>(() => {
  const thread = computedSelectedEmailThread.value;
  if (!thread) return [];
  return [thread, ...(thread.thread_emails || [])]
    .flatMap((email: any) => [email?.id, email?.provider_id])
    .filter(Boolean);
});

const emailICPOptions = computed(() => {
  const options = [{ value: "", label: "Select ICP for Email" }];
  props.icps.forEach((icp) => {
    options.push({
      value: icp.id,
      label: icp.isDefault ? `${icp.name} (Default)` : icp.name,
    });
  });
  return options;
});

// ─── Webhook setup ──────────────────────────────────────────────────────────
let webhooksCleanup: (() => void) | null = null;

// The list spans every connected account, so refresh on events for any of them —
// not just useEmailWebhooks' single-account `belongsToAccount` check.
function isOwnEmailAccount(payload: { accountId?: string }): boolean {
  return !!payload.accountId && connectedEmailAccountIds.value.includes(payload.accountId);
}

function setupEmailWebhooksHandler() {
  if (webhooksCleanup) {
    webhooksCleanup();
    webhooksCleanup = null;
  }

  const { setupSocket, cleanupSocket } = useEmailWebhooks({
    accountId: currentEmailAccount.value?.id,
    isEmailChannelSelected: true,
    onEmailReceived: (payload) => {
      try {
        if (!isOwnEmailAccount(payload)) return;
        queryClient.invalidateQueries({ queryKey: ["email-conversations"] });
      } catch (error) {
        console.error("Error handling email received event:", error);
      }
    },
    onEmailOpened: (payload) => {
      try {
        if (!isOwnEmailAccount(payload)) return;
        queryClient.invalidateQueries({ queryKey: ["email-conversations"] });
        if (
          computedSelectedEmailThread.value?.id === payload.emailId ||
          computedSelectedEmailThread.value?.provider_id === payload.emailId
        ) {
          nextTick(() => {
            if (emailViewerRef.value?.handleRealTimeOpen) {
              emailViewerRef.value.handleRealTimeOpen();
            }
            if (emailViewerRef.value?.fetchEmailReadReceipts) {
              emailViewerRef.value.fetchEmailReadReceipts();
            }
          });
        }
      } catch (error) {
        console.error("Error handling email opened event:", error);
      }
    },
    onEmailLinkClicked: (payload) => {
      try {
        if (!isOwnEmailAccount(payload)) return;
        queryClient.invalidateQueries({ queryKey: ["email-conversations"] });
        if (
          computedSelectedEmailThread.value?.id === payload.emailId ||
          computedSelectedEmailThread.value?.provider_id === payload.emailId
        ) {
          nextTick(() => {
            if (emailViewerRef.value?.handleRealTimeOpen) {
              emailViewerRef.value.handleRealTimeOpen();
            }
            if (emailViewerRef.value?.fetchEmailReadReceipts) {
              emailViewerRef.value.fetchEmailReadReceipts();
            }
          });
        }
      } catch (error) {
        console.error("Error handling email link clicked event:", error);
      }
    },
    showNotifications: false,
  });

  setupSocket();
  webhooksCleanup = cleanupSocket;
}

// ─── Watchers ────────────────────────────────────────────────────────────────
watch(
  () => props.pendingCueCardContent,
  (payload) => {
    if (payload && payload.channel === "email") {
      handleUseCueCardContent(payload);
      emit("content-applied");
    }
  }
);

watch(
  () => props.emailChannel,
  (newChannel) => {
    if (newChannel) {
      setupEmailWebhooksHandler();
    }
  },
  { immediate: true }
);

// Emit refresh-conversations when conversations data changes
watch(conversationsData, (newData) => {
  if (newData) {
    emit("refresh-conversations");
  }
});

let lastAppliedThreadId: string | null = null;

// Auto-opens the thread for an Interaction History click. The activity log only
// records "thread_id || email_id" (a single non-threaded email has no real thread_id,
// so its own id is reused as a stand-in) — we can't tell which one we got just by
// looking at it, and guessing wrong by always treating it as a thread_id causes
// getAllEmails({ thread_id }) to 404 for single emails. So when there's no local list
// match, probe: try it as a thread_id first, and only if that's not a real thread,
// fall back to looking it up as a single email by message_id.
async function resolveEmailThreadOrMessage(id: string, accountId: string): Promise<any | null> {
  try {
    const byThread = await Unipile.getAllEmails({ account_id: accountId, thread_id: id });
    if (byThread?.data?.data) return byThread.data.data;
  } catch (error: any) {
    if (error?.response?.status !== 404) {
      console.error("Failed to load email thread from Interaction History", error);
      return null;
    }
  }

  try {
    const byMessage = await Unipile.getAllEmails({
      account_id: accountId,
      message_id: id,
      meta_only: false,
    });
    const items = byMessage?.data?.data;
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
      items.find(
        (t: any) =>
          t.id === id || t.thread_emails?.some((e: any) => e.id === id || e.provider_id === id)
      ) || items[0]
    );
  } catch (error) {
    console.error("Failed to load email message from Interaction History", error);
    return null;
  }
}

// The thread can live in any connected account — try each in turn.
async function resolveAcrossAccounts(id: string, accountIds: string[]): Promise<any | null> {
  for (const accountId of accountIds) {
    const resolved = await resolveEmailThreadOrMessage(id, accountId);
    if (resolved) return { ...resolved, accountId };
  }
  return null;
}

watch(
  [() => props.initialThreadId, () => connectedEmailAccountIds.value.join(",")],
  async ([threadId]) => {
    const accountIds = connectedEmailAccountIds.value;
    if (!threadId || accountIds.length === 0) return;
    if (threadId === lastAppliedThreadId) return;

    const match = emailConversations.value.find(
      (t: any) => t.thread_id === threadId || t.id === threadId
    );
    if (match) {
      lastAppliedThreadId = threadId;
      handleEmailThreadSelected(match);
      return;
    }

    const resolved = await resolveAcrossAccounts(threadId, accountIds);
    // If initialThreadId moved on while this was in flight, leave lastAppliedThreadId
    // untouched so a later click back to this same id can still retry.
    if (threadId !== props.initialThreadId) return;
    lastAppliedThreadId = threadId;
    if (resolved) {
      handleEmailThreadSelected(resolved);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  if (webhooksCleanup) {
    webhooksCleanup();
  }
});

// ─── Handler functions ──────────────────────────────────────────────────────
function handleUseCueCardContent(payload: any) {
  if (payload.channel === "email") {
    aiGeneratedContent.value = payload.content || "";
    aiGeneratedSubject.value = payload.subject || "";
    replyingToEmail.value = null;
    isReplyAll.value = false;
    forwardingEmail.value = null;
    showEmailComposer.value = true;
    $toast.success("Email content loaded from cue card");
  } else {
    emit("switch-channel", payload);
  }
}

async function handleEmailAiReply(payload?: { email?: any; icpId?: string }) {
  if (isGeneratingAI.value) return;

  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    isGeneratingAI.value = true;
    generatingAction.value = "reply";
    try {
      const threadEmails = computedSelectedEmailThread.value
        ? computedSelectedEmailThread.value.thread_emails ||
          (computedSelectedEmailThread.value.is_thread ? [] : [computedSelectedEmailThread.value])
        : [];

      const userEmails = props.emailAccounts.map((acc: any) => acc.email || acc.name || "");
      const conversationHistory = normalizeEmailThread(threadEmails, userEmails);

      const contactId = props.selectedBucket?.contact?.id;
      const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

      const response = await AIGenerationService.generateReply({
        contactId,
        icpId,
        channel: "email",
        conversationHistory,
      });

      await nextTick();
      if (showEmailComposer.value && emailComposerRef.value) {
        const composer = emailComposerRef.value as any;
        if (composer.richTextEditor) {
          try {
            const { getQuillInstance } = await import("@/utils/emailComposerHelpers");
            const quill = getQuillInstance(composer.richTextEditor);
            if (quill?.root) {
              quill.root.innerHTML = response.body;
              if (composer.formData) composer.formData.body = response.body;
            }
          } catch (err) {
            console.warn("Error updating email composer:", err);
          }
        }
        // Don't let the AI's generated subject clobber the "Re: ..." subject that
        // ties this email to the existing thread (see AI Follow-up handler below).
        if (response.subject && composer.formData && !composer.formData.reply_to) {
          composer.formData.subject = response.subject;
        }
      } else {
        aiGeneratedContent.value = response.body;
        aiGeneratedSubject.value = response.subject || "";
        replyingToEmail.value = computedSelectedEmailThread.value;
        isReplyAll.value = false;
        showEmailComposer.value = true;
      }

      $toast.success("AI reply generated");
    } catch (error) {
      console.error("Failed to generate AI reply:", error);
      $toast.error("Failed to generate AI response");
    } finally {
      isGeneratingAI.value = false;
      generatingAction.value = null;
    }
  });
}

async function handleEmailAiFollowup(payload?: { email?: any; icpId?: string }) {
  if (isGeneratingAI.value) return;

  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    isGeneratingAI.value = true;
    generatingAction.value = "followup";
    try {
      const threadEmails = computedSelectedEmailThread.value
        ? computedSelectedEmailThread.value.thread_emails ||
          (computedSelectedEmailThread.value.is_thread ? [] : [computedSelectedEmailThread.value])
        : [];

      const userEmails = props.emailAccounts.map((acc: any) => acc.email || acc.name || "");
      const conversationHistory = normalizeEmailThread(threadEmails, userEmails);

      const contactId = props.selectedBucket?.contact?.id;
      const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

      const response = await AIGenerationService.generateFollowup({
        contactId,
        icpId,
        channel: "email",
        conversationHistory,
      });

      await nextTick();
      if (showEmailComposer.value && emailComposerRef.value) {
        const composer = emailComposerRef.value as any;
        if (composer.richTextEditor) {
          try {
            const { getQuillInstance } = await import("@/utils/emailComposerHelpers");
            const quill = getQuillInstance(composer.richTextEditor);
            if (quill?.root) {
              quill.root.innerHTML = response.body;
              if (composer.formData) composer.formData.body = response.body;
            }
          } catch (err) {
            console.warn("Error updating email composer:", err);
          }
        }
        // Reply/follow-up threading is driven by formData.reply_to, not the subject.
        // Keep the existing "Re: ..." subject so the message stays in the same thread
        // instead of being overwritten by a freshly-invented AI subject line.
        if (response.subject && composer.formData && !composer.formData.reply_to) {
          composer.formData.subject = response.subject;
        }
      } else {
        aiGeneratedContent.value = response.body;
        aiGeneratedSubject.value = response.subject || "";
        replyingToEmail.value = computedSelectedEmailThread.value;
        isReplyAll.value = false;
        showEmailComposer.value = true;
      }

      $toast.success("AI follow-up generated");
    } catch (error) {
      console.error("Failed to generate AI follow-up:", error);
      $toast.error("Failed to generate AI response");
    } finally {
      isGeneratingAI.value = false;
      generatingAction.value = null;
    }
  });
}

async function handleEmailAiCompose(payload?: { icpId?: string; customInstructions?: string }) {
  if (isGeneratingAI.value) return;

  await showUsageBasedFeaturesModalOnce("ai_generation", async () => {
    isGeneratingAI.value = true;
    generatingAction.value = "compose";
    try {
      const threadEmails = computedSelectedEmailThread.value
        ? computedSelectedEmailThread.value.thread_emails ||
          (computedSelectedEmailThread.value.is_thread ? [] : [computedSelectedEmailThread.value])
        : [];

      const userEmails = props.emailAccounts.map((acc: any) => acc.email || acc.name || "");
      const conversationHistory = normalizeEmailThread(threadEmails, userEmails);

      const contactId = props.selectedBucket?.contact?.id;
      const icpId = payload?.icpId ?? props.icps?.find((i) => i.isDefault)?.id;

      const response = await AIGenerationService.generateCompose({
        contactId,
        icpId,
        channel: "email",
        conversationHistory,
        customInstructions: payload?.customInstructions,
        aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
      });

      await nextTick();
      if (showEmailComposer.value && emailComposerRef.value) {
        const composer = emailComposerRef.value as any;
        if (composer.richTextEditor) {
          try {
            const { getQuillInstance } = await import("@/utils/emailComposerHelpers");
            const quill = getQuillInstance(composer.richTextEditor);
            if (quill?.root) {
              quill.root.innerHTML = response.body;
              if (composer.formData) composer.formData.body = response.body;
            }
          } catch (err) {
            console.warn("Error updating email composer:", err);
          }
        }
        if (response.subject && composer.formData && !composer.formData.reply_to) {
          composer.formData.subject = response.subject;
        }
      } else {
        aiGeneratedContent.value = response.body;
        aiGeneratedSubject.value = response.subject || "";
        replyingToEmail.value = null;
        isReplyAll.value = false;
        showEmailComposer.value = true;
      }

      $toast.success("AI message composed");
    } catch (error) {
      console.error("Failed to generate AI compose:", error);
      $toast.error("Failed to generate AI response");
    } finally {
      isGeneratingAI.value = false;
      generatingAction.value = null;
    }
  });
}

function handleEmailReply(email: any) {
  replyingToEmail.value = email;
  isReplyAll.value = false;
  showEmailComposer.value = true;
}

function handleEmailForward(email: any) {
  forwardingEmail.value = email;
  showEmailComposer.value = true;
}

function handleEmailDelete(email: any) {
  emailToDelete.value = email;
  showDeleteConfirmation.value = true;
}

async function confirmEmailDelete() {
  const email = emailToDelete.value;
  if (!email) return null;

  const accountId = accountIdFor(email);
  if (!accountId) return null;

  try {
    await deleteEmail({
      conversationId: email.id,
      emailId: email.provider_id || email.id,
      threadId: email.thread_id,
      isThread: !!(email.is_thread && email.thread_id),
      accountId,
    });

    if (computedSelectedEmailThread.value?.id === email.id) {
      selectedEmailThread.value = null;
      selectedEmailThreadId.value = null;
      showEmailDetailView.value = false;
    }

    $toast.success("Email deleted successfully");
    return true;
  } catch (error: any) {
    console.error("Failed to delete email:", error);
    $toast.error(error.response?.data?.message || "Failed to delete email");
    throw error;
  }
}

function handleDeleteConfirmationClose() {
  showDeleteConfirmation.value = false;
  emailToDelete.value = null;
}

async function handleEmailToggleReadStatus(email: any) {
  try {
    const accountId = accountIdFor(email);
    if (!accountId) return;

    const currentlyRead =
      email.is_thread && email.thread_metadata
        ? (email.thread_metadata.unread_count || 0) === 0
        : !!email.read_date;

    const shouldMarkAsUnread = currentlyRead;

    await updateReadStatus({
      conversationId: email.id,
      emailId: email.provider_id || email.id,
      threadId: email.thread_id,
      isThread: !!(email.is_thread && email.thread_id),
      accountId,
      markAsUnread: shouldMarkAsUnread,
    });

    // Also update locally-held selectedEmailThread if it matches
    if (computedSelectedEmailThread.value?.id === email.id && selectedEmailThread.value) {
      if (email.is_thread && selectedEmailThread.value.thread_metadata) {
        selectedEmailThread.value.thread_metadata.unread_count = shouldMarkAsUnread
          ? selectedEmailThread.value.thread_metadata.total_emails || 1
          : 0;
        if (selectedEmailThread.value.thread_emails) {
          selectedEmailThread.value.thread_emails.forEach((e: any) => {
            e.read_date = shouldMarkAsUnread ? null : e.read_date || new Date().toISOString();
          });
        }
      } else {
        selectedEmailThread.value.read_date = shouldMarkAsUnread ? null : new Date().toISOString();
      }
    }

    $toast.success(shouldMarkAsUnread ? "Email marked as unread" : "Email marked as read");
  } catch (error: any) {
    console.error("Failed to toggle email read status:", error);
    $toast.error(error.response?.data?.message || "Failed to update email");
  }
}

function openEmailComposer() {
  showEmailComposer.value = true;
}

function handleEmailComposerClose() {
  showEmailComposer.value = false;
  replyingToEmail.value = null;
  isReplyAll.value = false;
  forwardingEmail.value = null;
  aiGeneratedContent.value = "";
  aiGeneratedSubject.value = "";
}

async function handleEmailComposerSend(payload: any, done?: (success: boolean) => void) {
  let attempted = false;
  await showUsageBasedFeaturesModalOnce("email", async () => {
    attempted = true;
    try {
      await sendEmail({ payload });
      $toast.success("Email sent successfully");
      handleEmailComposerClose();
      emit("email-sent");
      if (typeof done === "function") done(true);
    } catch (error: any) {
      console.error("Failed to send email:", error);
      if (!error?.absorbedButRecoverable) {
        $toast.error(error.response?.data?.message || "Failed to send email");
      }
      if (typeof done === "function") done(false);
    }
  });
  if (!attempted && typeof done === "function") done(false);
}

async function handleEmailComposerSaveDraft(payload: any) {
  try {
    await saveDraft({ payload });
    $toast.success("Draft saved successfully");
    handleEmailComposerClose();
  } catch (error: any) {
    console.error("Failed to save draft:", error);
    $toast.error(error.response?.data?.message || "Failed to save draft");
  }
}

async function handleEmailComposerScheduleSend(payload: any) {
  try {
    await scheduleEmail({ payload });
    $toast.success("Email scheduled successfully");
    handleEmailComposerClose();
  } catch (error: any) {
    console.error("Failed to schedule email:", error);
    $toast.error(error.response?.data?.message || "Failed to schedule email");
  }
}

async function markEmailEngagementNotificationsSeen(thread: any) {
  const contactId = props.selectedBucket?.contact?.id;
  if (!contactId) return;

  // Send the thread id plus every message's Unipile id. The webhook stores `thread_id`
  // only when Unipile supplies one, so an event saved without it can only be matched
  // by `email_id` — which is Unipile's `id`, not the provider's `provider_id`.
  const threadId = thread.thread_id || undefined;
  const emailIds = [
    ...new Set(
      [thread.id, ...(thread.thread_emails || []).map((e: any) => e?.id)].filter(
        Boolean
      ) as string[]
    ),
  ];
  if (!threadId && emailIds.length === 0) return;

  try {
    await Contact.markEmailNotificationsSeen(contactId, { threadId, emailIds });
    // Buckets table's unread-email badge is a separate bulk query, keyed by
    // the page's contactIds — invalidate by prefix so it refetches too.
    queryClient.invalidateQueries({ queryKey: ["engagement-summary"] });
  } catch (error) {
    console.error("Failed to mark email notifications as seen:", error);
  }
}

async function handleEmailThreadSelected(thread: any) {
  selectedEmailThreadId.value = thread.id;
  showEmailDetailView.value = true;
  selectedEmailThread.value = thread;
  isLoadingMessages.value = true;
  markEmailEngagementNotificationsSeen(thread);

  try {
    const accountId = accountIdFor(thread);
    if (!accountId) {
      console.error("No account ID available");
      selectedEmailThread.value = thread;
      isLoadingMessages.value = false;
      return;
    }

    if (thread.thread_id) {
      const response = await Unipile.getAllEmails({
        account_id: accountId,
        thread_id: thread.thread_id,
      });
      // Keep the owning account on the loaded thread so reply/delete/read use it.
      selectedEmailThread.value = { ...response.data.data, accountId };

      nextTick(() => {
        if (emailViewerRef.value?.fetchEmailReadReceipts) {
          emailViewerRef.value.fetchEmailReadReceipts();
        }
      });
    } else {
      selectedEmailThread.value = thread;

      nextTick(() => {
        if (emailViewerRef.value?.fetchEmailReadReceipts) {
          emailViewerRef.value.fetchEmailReadReceipts();
        }
      });
    }

    // Mark as read if unread
    if (thread.is_thread && thread.thread_metadata && thread.thread_metadata.unread_count > 0) {
      try {
        await Unipile.updateThread(thread.thread_id, { unread: false }, accountId);
        // Update conversations cache optimistically
        queryClient.setQueriesData<any[]>({ queryKey: ["email-conversations"] }, (old) => {
          if (!old) return old;
          return old.map((conv) => {
            if (conv.id !== thread.id) return conv;
            return {
              ...conv,
              isRead: true,
              unreadCount: 0,
              thread_metadata: { ...conv.thread_metadata, unread_count: 0 },
            };
          });
        });
        if (selectedEmailThread.value?.thread_metadata) {
          selectedEmailThread.value.thread_metadata.unread_count = 0;
          if (selectedEmailThread.value.thread_emails) {
            selectedEmailThread.value.thread_emails.forEach((email: any) => {
              email.read_date = email.read_date || new Date().toISOString();
            });
          }
        }
      } catch (error) {
        console.error("Failed to mark thread as read:", error);
      }
    } else if (!thread.is_thread && !thread.read_date) {
      try {
        const emailId = thread.provider_id || thread.id;
        await Unipile.updateEmail(emailId, { unread: false }, accountId);
        queryClient.setQueriesData<any[]>({ queryKey: ["email-conversations"] }, (old) => {
          if (!old) return old;
          return old.map((conv) => {
            if (conv.id !== thread.id) return conv;
            return {
              ...conv,
              isRead: true,
              unreadCount: 0,
              read_date: new Date().toISOString(),
            };
          });
        });
      } catch (error) {
        console.error("Failed to mark email as read:", error);
      }
    }
  } catch (error: any) {
    console.error("Failed to load email thread:", error);
    if (error.response?.status === 404) {
      $toast.error("Thread not found");
    } else {
      $toast.error(error.response?.data?.message || "Failed to load email thread");
    }
    selectedEmailThread.value = thread;
  } finally {
    isLoadingMessages.value = false;
  }
}

function handleEmailBackToList() {
  selectedEmailThreadId.value = null;
  showEmailDetailView.value = false;
}

// ─── AI reply drafts ────────────────────────────────────────────────────────
const isEmail = (email: any, emailId: string) =>
  email?.id === emailId || email?.provider_id === emailId;

/** Opens the thread holding the email an AI reply draft answers. */
function openThreadForEmail(emailId: string) {
  const thread = emailConversations.value.find(
    (conv: any) =>
      isEmail(conv, emailId) || conv.thread_emails?.some((e: any) => isEmail(e, emailId))
  );
  if (thread) handleEmailThreadSelected(thread);
  else $toast.error("That email isn't in this list anymore.");
}

/** Puts an AI reply draft in the composer, answering the email it was written for. */
function useReplyDraft(draft: { text: string; replyToEmailId?: string }) {
  const thread = computedSelectedEmailThread.value;
  const emails = [thread, ...(thread?.thread_emails || [])];
  replyingToEmail.value =
    (draft.replyToEmailId && emails.find((e: any) => isEmail(e, draft.replyToEmailId!))) || thread;
  forwardingEmail.value = null;
  isReplyAll.value = false;
  aiGeneratedContent.value = draft.text;
  aiGeneratedSubject.value = "";
  showEmailComposer.value = true;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.isChannelsLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(LoadingBars, { size: "sm" })
        ]))
      : (!isEmailAccountConnected.value || isEmailChannelDisconnected.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(Icon), {
                icon: "mdi:link-variant-off",
                class: "email-disconnected-icon"
              }),
              _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "email-disconnected-title" }, "Not Connected", -1)),
              _cache[3] || (_cache[3] = _createElementVNode("p", { class: "email-disconnected-message" }, " Please connect your email account to view emails. ", -1)),
              _createElementVNode("button", {
                class: "email-disconnected-action",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('connect-account')))
              }, [
                _createVNode(_unref(Icon), { icon: "mdi:link" }),
                _cache[1] || (_cache[1] = _createTextVNode(" Connect Account ", -1))
              ])
            ])
          ]))
        : (!showEmailDetailView.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (__props.emailChannel)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createVNode(_unref(Button), {
                      variant: "primary",
                      size: "sm",
                      icon: "mdi:email-edit-outline",
                      onClick: openEmailComposer,
                      block: "",
                      disabled: !isEmailAccountConnected.value,
                      class: "compose-btn"
                    }, {
                      default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                        _createTextVNode(" Compose ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["disabled"])
                  ]))
                : _createCommentVNode("", true),
              _createVNode(OutreachCueCards, {
                "icp-options": emailICPOptions.value,
                "icp-loading": props.icpLoading,
                "contact-id": __props.selectedBucket?.contact?.id || '',
                "contact-name": 
          [__props.selectedBucket?.contact?.fname, __props.selectedBucket?.contact?.lname].filter(Boolean).join(' ')
        ,
                "full-width": true,
                onUseContent: handleUseCueCardContent
              }, null, 8, ["icp-options", "icp-loading", "contact-id", "contact-name"]),
              _createVNode(AgentReplyDraft, {
                "contact-id": __props.selectedBucket?.contact?.id,
                channel: "email",
                "show-open": "",
                onOpen: openThreadForEmail
              }, null, 8, ["contact-id"]),
              _createVNode(ConversationList, {
                conversations: emailConversations.value,
                "selected-conversation-id": selectedEmailThreadId.value ?? undefined,
                "is-loading": _unref(isLoadingConversations),
                type: "email",
                "show-search": false,
                onConversationSelected: handleEmailThreadSelected
              }, null, 8, ["conversations", "selected-conversation-id", "is-loading"])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (computedSelectedEmailThread.value)
                ? (_openBlock(), _createBlock(EmailViewer, {
                    key: `email-viewer-${computedSelectedEmailThread.value?.id || 'none'}`,
                    ref_key: "emailViewerRef",
                    ref: emailViewerRef,
                    email: computedSelectedEmailThread.value,
                    "is-loading": isLoadingMessages.value,
                    icps: __props.icps,
                    "is-generating-a-i": isGeneratingAI.value,
                    "generating-action": generatingAction.value,
                    onReply: handleEmailReply,
                    onForward: handleEmailForward,
                    onDelete: handleEmailDelete,
                    onMarkAsRead: handleEmailToggleReadStatus,
                    onAiReply: handleEmailAiReply,
                    onAiFollowup: handleEmailAiFollowup
                  }, {
                    "back-button": _withCtx(() => [
                      _createVNode(_unref(Button), {
                        size: "xs",
                        variant: "ghost",
                        rounded: "",
                        icon: "mdi:chevron-left",
                        class: "back-to-list-btn",
                        onClick: handleEmailBackToList
                      }, {
                        default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                          _createTextVNode(" Back ", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["email", "is-loading", "icps", "is-generating-a-i", "generating-action"]))
                : _createCommentVNode("", true),
              (computedSelectedEmailThread.value)
                ? (_openBlock(), _createBlock(AgentReplyDraft, {
                    key: 1,
                    "contact-id": __props.selectedBucket?.contact?.id,
                    channel: "email",
                    "email-ids": selectedThreadEmailIds.value,
                    onUse: useReplyDraft
                  }, null, 8, ["contact-id", "email-ids"]))
                : _createCommentVNode("", true),
              (!computedSelectedEmailThread.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _createVNode(_unref(Button), {
                      size: "sm",
                      variant: "ghost",
                      rounded: "",
                      icon: "mdi:chevron-left",
                      class: "back-to-list-btn email-empty-state__back",
                      onClick: handleEmailBackToList
                    }, {
                      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                        _createTextVNode(" Back ", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[7] || (_cache[7] = _createElementVNode("p", null, "Select an email thread to view messages", -1))
                  ]))
                : _createCommentVNode("", true)
            ])),
    (showEmailComposer.value)
      ? (_openBlock(), _createBlock(EmailComposer, {
          key: `email-composer-${replyingToEmail.value?.id || forwardingEmail.value?.id || 'new'}`,
          ref_key: "emailComposerRef",
          ref: emailComposerRef,
          show: showEmailComposer.value,
          "reply-to": replyingToEmail.value,
          "reply-all": isReplyAll.value,
          "forward-email": forwardingEmail.value,
          "default-recipient": __props.selectedBucket?.contact?.email || '',
          accounts: __props.emailAccounts,
          "default-account-id": composerAccountId.value,
          "initial-body": aiGeneratedContent.value,
          "initial-subject": aiGeneratedSubject.value,
          "show-ai-buttons": true,
          "is-thread": !!replyingToEmail.value || !!forwardingEmail.value,
          "ai-buttons-disabled": !isEmailAccountConnected.value || _unref(isUsageBlocked),
          "is-generating-a-i": isGeneratingAI.value,
          "generating-action": generatingAction.value,
          "contact-id": __props.selectedBucket?.contact?.id,
          "icp-id": __props.icps?.find((i) => i.isDefault)?.id,
          icps: __props.icps,
          onClose: handleEmailComposerClose,
          onSend: handleEmailComposerSend,
          onSaveDraft: handleEmailComposerSaveDraft,
          onScheduleSend: handleEmailComposerScheduleSend,
          onAiReply: handleEmailAiReply,
          onAiFollowup: handleEmailAiFollowup,
          onAiCompose: handleEmailAiCompose
        }, null, 8, ["show", "reply-to", "reply-all", "forward-email", "default-recipient", "accounts", "default-account-id", "initial-body", "initial-subject", "is-thread", "ai-buttons-disabled", "is-generating-a-i", "generating-action", "contact-id", "icp-id", "icps"]))
      : _createCommentVNode("", true),
    (showDeleteConfirmation.value)
      ? (_openBlock(), _createBlock(DeleteConfirmationModal, {
          key: 5,
          entity: "email",
          "delete-function": confirmEmailDelete,
          onCloseDeleteConfirmationModal: handleDeleteConfirmationClose
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})